import { Injectable } from '@angular/core';
import {
  LoadCurrentStorefrontQuery,
  LoadStaticPathsListQuery,
  LoadStorefrontActiveContractsQuery,
  LoadStorefrontDetailsQuery,
  LoadStorefrontListQuery,
  LoadStorefrontRedirectablesQuery,
  LoadStorefrontRevenueListQuery,
  LoadStorefrontRevenueRecordsQuery,
  LoadStorefrontRevenueTotalsQuery,
  LoadStorefrontSettingsQuery,
  LoadStorefrontTagFieldsQuery,
  StorefrontSectorsEnum,
  TagFieldScopeEnum,
  UpdateStorefrontInput,
  UpdateStorefrontMutation
} from '@queries/generated';
import { finalMap } from '@rx/final-map';
import { startOf } from '@util/date';
import { map, startWith } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StorefrontsService {
  current$ = this.loadCurrentStorefrontQuery.watch().valueChanges.pipe(map(({ data }) => data.currentStorefront));
  canCreateOrder$ = this.current$.pipe(
    map((currentStorefront) => currentStorefront.can.createOrder),
    startWith(false)
  );

  list$ = this.loadStorefrontListQuery.watch().valueChanges.pipe(map(({ data }) => data.storefronts.all));
  staticPaths$ = this.loadStaticPathsListQuery.watch().valueChanges.pipe(map(({ data }) => data.staticPaths));

  constructor(
    private loadCurrentStorefrontQuery: LoadCurrentStorefrontQuery,
    private loadStorefrontTagFieldsQuery: LoadStorefrontTagFieldsQuery,
    private loadStaticPathsListQuery: LoadStaticPathsListQuery,
    private loadStorefrontListQuery: LoadStorefrontListQuery,
    private loadStorefrontRevenueTotalsQuery: LoadStorefrontRevenueTotalsQuery,
    private loadStorefrontRevenueRecordsQuery: LoadStorefrontRevenueRecordsQuery,
    private loadStorefrontRevenueListQuery: LoadStorefrontRevenueListQuery,
    private loadStorefrontDetailsQuery: LoadStorefrontDetailsQuery,
    private loadStorefrontRedirectablesQuery: LoadStorefrontRedirectablesQuery,
    private loadStorefrontActiveContractsQuery: LoadStorefrontActiveContractsQuery,
    private loadStorefrontSettingsQuery: LoadStorefrontSettingsQuery,
    private updateStorefrontMutation: UpdateStorefrontMutation
  ) {}

  find = (id: number) => {
    return this.loadStorefrontDetailsQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.storefronts.findById));
  };

  settings = (id: number) => {
    return this.loadStorefrontSettingsQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.storefronts.findById));
  };

  redirectables(id: number) {
    return this.loadStorefrontRedirectablesQuery
      .watch({ id })
      .valueChanges.pipe(map(({ data }) => data.storefronts.findById));
  }

  // TODO: Maybe receive the storefront id as param if we want to reuse it
  // For now, getting for the current storefront.
  tagFields = (scope: TagFieldScopeEnum = null) => {
    return this.loadStorefrontTagFieldsQuery
      .watch({ scope })
      .valueChanges.pipe(map(({ data }) => data.currentStorefront.tagFields));
  };

  /**
   * Fetch a storefront by gid with its total revenues, to an informed period.
   *
   * @param id - Storefront GID
   * @param startDate - Data to start the search
   * @param endDate - Date to end the search
   */
  storefrontRevenueTotals = (id: string, startDate: Date = startOf('month'), endDate: Date = new Date()) => {
    return this.loadStorefrontRevenueTotalsQuery
      .watch({ id, startDate, endDate })
      .valueChanges.pipe(map(({ data }) => data.storefronts.findByGid));
  };

  /**
   * Fetch a storefront by gid with all the records for each revenue
   *
   * @param id - Storefront GID
   * @param startDate - Data to start the search
   * @param endDate - Date to end the search
   * @param onlineTraining - If the online training records should be returned
   * @param ilTraining - If the il training records should be returned
   * @param physicalProduct - If the physical products records should be returned
   * @param promoCodeSaving - If the promo code saving records should be returned
   * @param shipping - if the shipping records should be returned
   *
   * @param platformSetup - If the platform setup records should be returned
   * @param platformLicenses - If the platform licenses records should be returned
   * @param transactionFees - If the transaction fees records should be returned
   * @param courseDistribution - If the course distribution records should be returned
   *
   * @param subscription - If the subscription records should be returned
   * @param thirdPartyPayout - If the third-party payout records should be returned
   * @param refunds - If the refunds records should be returned
   *
   * @param federalTaxes - If the federal taxes records should be returned
   * @param provincialTaxes - If the provincial taxes records should be returned
   */
  storefrontRevenueRecords = (
    id: string,
    startDate = startOf('month'),
    endDate = new Date(),
    onlineTraining = false,
    ilTraining = false,
    physicalProduct = false,
    promoCodeSaving = false,
    shipping = false,

    platformSetup = false,
    platformLicenses = false,
    transactionFees = false,
    courseDistribution = false,

    subscription = false,
    thirdPartyPayout = false,
    refunds = false,

    federalTaxes = false,
    provincialTaxes = false
  ) => {
    return this.loadStorefrontRevenueRecordsQuery
      .watch({
        id,
        startDate,
        endDate,
        onlineTraining,
        ilTraining,
        physicalProduct,
        promoCodeSaving,
        shipping,

        platformSetup,
        platformLicenses,
        transactionFees,
        courseDistribution,

        subscription,
        thirdPartyPayout,
        refunds,

        federalTaxes,
        provincialTaxes
      })
      .valueChanges.pipe(map(({ data }) => data.storefronts.findByGid));
  };

  /**
   * Fetch a storefront by gid with all the active contracts for a date period
   *
   * @param id - Storefront GID
   * @param startDate -
   * @param endDate
   */
  storefrontActiveContracts = (id: string, startDate = startOf('month'), endDate = new Date()) => {
    return this.loadStorefrontActiveContractsQuery
      .watch({
        id,
        startDate,
        endDate
      })
      .valueChanges.pipe(map(({ data }) => data.storefronts.findByGid));
  };

  /**
   * @param sector - Search storefronts for the informed  sector
   * @param currency - Search storefronts for the informed currency
   * @param startDate - Data to start the search
   * @param endDate - Date to end the search
   */
  listRevenues = (
    sector: StorefrontSectorsEnum,
    currency: string,
    startDate = startOf('month'),
    endDate = new Date(),
    showAll = true,
    active = true
  ) => {
    return this.loadStorefrontRevenueListQuery
      .watch({ sector, currency, startDate, endDate, showAll, active })
      .valueChanges.pipe(map(({ data }) => data.storefronts.all));
  };

  update(input: UpdateStorefrontInput) {
    return this.updateStorefrontMutation.mutate({ input }).pipe(
      map(({ data }) => data.updateStorefront),
      finalMap('storefront')
    );
  }
}
